/**
 * PosterFrameButtons.js
 *
 * @description Defines a mixin for common actions inside poster frames.
 *
 * It provides a list of identifiers, and a corresponding set of buttons.
 * Each button comes with a handler function which is implemented here.
 *
 * Here are the general steps we need to take:
 * 1. Adopt the mixin in our component.
 * 2. Tell the mixin which frontend/detectionID face uuids to use.
 * 3. Assign callback handlers.
 *
 * # 1. Adopt the Mixin
 * There are three steps to adopting the mixin:
 *
 * i. Import it
 * ii. Add to your component definition
 * iii. Pass the buttons in your template.
 *
 * Let's walk through an example.
 *
 * You can import the mixin with the following line of code in your component:

   import {PosterFrameButtons} from '@/mixins'

* Then, in your component, add a field called `mixins`.

export default {
  name: 'my-component',
  mixins: [PosterFrameButtons], // <---  NOTE THIS LINE
  components: { ... },
}

 *
 *  Your component will now have all of the properties and methods in this mixin.
 * As a result, you can pass `buttons` to an OwalPosterFrame` and it will show the
 * buttons that you might expect to appear.
 * Here's an example:
 *

 <template>
  <div>
    <owal-event-banner-poster-frame
    :poster-frame-small="smallImageURL"
    :poster-frame-large="largeImageURL"
    :title="title"
    ...
    :buttons="buttons"
    />
  </div>
 </template>

 *
 * There's one more required implementation step.
 * We need to tell the mixin which uuids to use, and optionally pass it a handler.
 * We can do this by overriding the computed property called `context`.
 * The `context ()` property returns an object, which looks like this:
 *

    {
      video: {
        timestamp: undefined,
        cameraUUID: undefined,
      },
      face: {
        detectionID: undefined,
        frontendUUID: undefined,
        confirmed: false,
      },
      handlers: {
        confirmFace: undefined,
        notAFace: undefined,
        undoConfirm: undefined,
        error: undefined,
      }
    }

 * The default implementation returns exactly this object, which means the buttons won't do anything.
 * If we want the video button to work, we can do the following:

<script>
import {PosterFrameButtons} from '@/mixins'
export default {
  name: 'my-component',
  mixins: [PosterFrameButtons]
  computed: {
    context () {
      return   {
        video: {
          timestamp: moment(...).utc().format(),
          cameraUUID: a_camera_uuid
        },
        // Rest of context object here...
      }
    }
  }
}
</script>

 *
 *
 * To enable the tagging buttons, pass in a `face` object with `detectionID` and `frontendUUID`:

 <script>
 import {PosterFrameButtons} from '@/mixins'
export default {
  name: 'my-component',
  mixins: [PosterFrameButtons]
  computed: {
    context () {
      return   {
        face: {
          detectionId: something.detectionID,
          frontendUUID: something.frontend_face_uuid,
        }
      }
    }
  }
}
</script>

 * That's it!
 *
 * You may be wondering what the deal is with `context.face.confirmed` as well as the handlers.
 * The confirmed property is a boolean flag that tracks the last known state of the face. (Is it a confirmed match?)
 *
 * The handlers are optional, and they are called inside the callback for the network requests.
 * Valid handlers are: `confirmFace`, `notAFace`, `wrongPerson`, `undoConfirm`, and `error`. For example:

<script>
import {PosterFrameButtons} from '@/mixins'
export default {
  name: 'my-component',
  mixins: [PosterFrameButtons]
  computed: {
    context () {
      return   {
        face: {
          detectionID: something.detectionID,
          frontendUUID: something.frontend_face_uuid,
        },
        handlers: {
          confirmFace: this.updateConfirmedUI,
          error: this.handleError,
          ...
        }
      }
    }
  }
}
</script>

 * This should get you started. If you have any questions or feedback, ping moshe@owal.io or Moshe on Slack.
 *
 */

import { OwalButtonConfiguration } from '@/libraries/owal-ui'
import { faceconfirm, facematchconfirm, imageForDetectionID } from '@/libraries/api/faces'

export const Identifiers = {
  VIDEO: 'video',
  CONFIRM: 'confirm',
  UNDO_CONFIRM: 'undo',
  WRONG_PERSON: 'wrong-person',
  NOT_A_FACE: 'not-a-face',
}

/**
 * @mixin PosterFrameButtons
 *
 * @description If you want to control the buttons that appear in your poster frame,
 * adopt this mixin by adding it to your component's `mixins: [...]` array.
 *
 * Then, override the `buttonIdentifiers` computed property.
 */
export const PosterFrameButtons = {
  data () {
    return {
      confirmed: false,
      removed: false,
      videoButton: new OwalButtonConfiguration({
        identifier: Identifiers.VIDEO,
        title: 'Video',
        accessoryType: 'none',
        callback: this.goToPosterFrameVideo,
        alignment: 'center',
      }),
      confirmButton: new OwalButtonConfiguration({
        identifier: Identifiers.CONFIRM,
        title: 'Confirm',
        accessoryType: 'none',
        callback: this.setPosterFrameMatchConfirmed,
        destructive: false,
        alignment: 'center',
      }),
      undoConfirmButton: new OwalButtonConfiguration({
        identifier: Identifiers.UNDO_CONFIRM,
        title: 'Undo',
        accessoryType: 'none',
        callback: this.undoPosterFrameConfirm,
        destructive: true,
        alignment: 'center',
      }),
      wrongPersonButton: new OwalButtonConfiguration({
        identifier: Identifiers.WRONG_PERSON,
        title: 'Wrong Person',
        destructive: true,
        accessoryType: 'none',
        callback: this.setPosterFrameMatchIncorrect,
        alignment: 'center',
      }),
      notAFaceButton: new OwalButtonConfiguration({
        identifier: Identifiers.NOT_A_FACE,
        title: 'Not a Face',
        destructive: true,
        accessoryType: 'none',
        callback: this.setPosterFrameNotFace,
        alignment: 'center',
      }),
    }
  },
  computed: {
    /** Decides which identifier to use for the undo button. */
    confirmButtonToUse () {
      return this.context.face.confirmed
        ? Identifiers.UNDO_CONFIRM
        : Identifiers.CONFIRM
    },
    /**
     * @returns A list of identifiers which are used to filter buttons.
     */
    buttonIdentifiers () {
      return [
        Identifiers.VIDEO,
        this.confirmButtonToUse,
        Identifiers.WRONG_PERSON,
        Identifiers.NOT_A_FACE,
      ]
    },
    /**
     * Filters buttons based on buttonIdentifiers and returns these.
     *
     * @returns An array of `OwalButtonConfiguration` objects.
     */
    buttons () {
      let buttons = [
        this.videoButton,
        this.confirmButton,
        this.undoConfirmButton,
        this.wrongPersonButton,
        this.notAFaceButton,
      ].filter(button => this.buttonIdentifiers.includes(button.identifier))
      return buttons
    },
    /**
     * A default context object for the mixin.
     *
     * @returns
     */
    defaultContext () {
      return {
        video: {
          timestamp: undefined,
          cameraUUID: undefined,
        },
        face: {
          frontendUUID: undefined,
          confirmed: false,
          detectionId: undefined,
          domainUUID: undefined,
        },
        handlers: {
          confirmFace: undefined,
          notAFace: undefined,
          undoConfirm: undefined,
          error: undefined,
          notAMatch: undefined,
        },
      }
    },
    context () {
      return this.defaultContext
    },
  },
  methods: {
    goToPosterFrameVideo () {
      const timestamp = this.context.video.timestamp
      const cameraUUID = this.context.video.cameraUUID
      this.$router.push({
        path: `/view/${cameraUUID}/${timestamp}`,
      })
    },
    setPosterFrameNotFace () {
      return faceconfirm(this.context.face.detectionId, false, this.context.face.domainUUID)
        .then(res => res.data)
        .then(data => {
          if (data.ok) {
            if (this.context.handlers.notAFace) {
              this.context.handlers.notAFace(data)
            }
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    setPosterFrameMatchConfirmed () {
      return this.setConfirmed(
        true,
        this.context.handlers.confirmFace,
        this.context.handlers.error,
        this.context.face.domainUUID,
      )
    },
    setPosterFrameMatchIncorrect () {
      console.log('not a match button')
      return this.setConfirmed(
        false,
        this.context.handlers.notAMatch,
        this.context.handlers.error,
        this.context.face.domainUUID,
      )
    },
    undoPosterFrameConfirm () {
      return this.setConfirmed(
        undefined,
        this.context.handlers.undoConfirm,
        this.context.handlers.error,
        this.context.face.domainUUID,
      )
    },
    /**
     * Calls the facematchconfirm API.
     *
     * @param {Bool} [confirmed=undefined] `true` means "this is correct", `false` is "wrong" person, `undefined` is "undo."
     * @param {Function} successHandler Executed upon successful completion of the request. Takes one argument: `data`.
     * @param {Function} errorHandler Executed upon failed completion of the request. Takes one argument: `error`
     * @returns A promise that resolves to a network response.
     */
    setConfirmed (confirmed = undefined, successHandler, errorHandler, domainUUID) {
      return facematchconfirm(
        this.context.face.detectionId,
        this.context.face.frontendUUID,
        confirmed,
        domainUUID,
      )
        .then(res => res.data)
        .then(data => {
          if (data.ok) {
            this.confirmed = confirmed
            if (successHandler) {
              successHandler(confirmed)
            }
          }
        })
        .catch(error => {
          if (errorHandler) {
            errorHandler(error)
          }
        })
    },
    posterFrameFromDetection (face, shouldShowBestMatch = true) {

      if (face.is_track_step) {
        const url = `https://svc-faces-track-img.api.owal.io/track/${face.track_uuid}/step_ts/${face.captured_at}`.replace('Z','')
        return {
         thumbnail: url + '?cropped=1&box=0',
         poster: url + '?cropped=0&box=1',
        }
      }

      const detectionId = (face.bestMatch && face.bestMatch.detection_id && shouldShowBestMatch) ? face.bestMatch.detection_id : face.detection_id
      const thumbnail = imageForDetectionID(detectionId)
      const poster = (face.bestMatch && face.bestMatch.frame_uri && shouldShowBestMatch) ? face.bestMatch.frame_uri : face.frame_uri
      const matchConfidence = (face.bestMatch && face.bestMatch.match_confidence && shouldShowBestMatch) ? face.bestMatch.match_confidence : face.match_confidence
      const faceConfidence = (face.bestMatch && face.bestMatch.face_confidence && shouldShowBestMatch) ? face.bestMatch.face_confidence : face.face_confidence
      const matchConfidenceLevel = (face.bestMatch && face.bestMatch.match_confidence_level && shouldShowBestMatch) ? face.bestMatch.match_confidence_level : face.match_confidence_level
      const suppressed = face.is_suppressed || (face.merged && face.merged.some(x => x.is_suppressed))
      const hasAllRequiredFields = !!detectionId && !!thumbnail && !!poster
      const faceHeight = (face.bestMatch && face.bestMatch.face_height && shouldShowBestMatch) ? face.bestMatch.face_height : face.face_height
      const maskLabel = (face.bestMatch && face.bestMatch.mask_label && shouldShowBestMatch) ? face.bestMatch.mask_label : face.mask_label
      const maskLabelConfidence = (face.bestMatch && face.bestMatch.mask_label_confidence && shouldShowBestMatch) ? face.bestMatch.mask_label_confidence : face.mask_label_confidence

      if (!hasAllRequiredFields) {
        return undefined
      }

      // Confidence is matchConfidence
      const frame = {
        thumbnail,
        poster,
        detectionId,
        confidence: matchConfidence,
        matchConfidence,
        faceConfidence,
        matchConfidenceLevel,
        suppressed,
        faceHeight,
        maskLabel,
        maskLabelConfidence,
      }

      // console.debug(`Frame: ${frame}`)
      return frame
    },
    subtitleFromPosterFrame (entry) {
      const info = [
        `Match conf. ${this.formattedConfidence(entry.matchConfidence)}%`,
        `Face conf. ${this.formattedConfidence(entry.faceConfidence)}%`,
        `Face height ${entry.faceHeight}`,
      ]
      if (entry.maskLabel) {
        let label
        switch(entry.maskLabel) {
          case 'no_mask': label = 'No Mask'; break;
          case 'mask_ok': label = 'Has mask'; break;
        }
        if (label) {
          info.push(`${label}, ${this.formattedConfidence(entry.maskLabelConfidence)}%`)
        }
      }
      return info.join(' | ')
    },
    formattedConfidence (confidence) {
      if (confidence === null || confidence === undefined) {
        return ''
      }
      let conf = `${(parseFloat(confidence) * 100).toFixed(2)}`

      if (parseInt(confidence) === 1) {
        conf = '100'
      }
      return conf
    },
  },
}
